// Noyau — FEUILLES JOUÉES PAR L'ANCIEN MOTEUR COMPLET : systèmes d'équations (`kernel.system`), hérédité d'une
// récurrence (`kernel.induction`), encadrements (plugin « bounds »).
//
// Ces sortes vivent dans le juge général de l'ancien moteur (`packages/reasoner/src/reasoner.ts`, lots 5.1, 5.5,
// 6.4), transcrit sans changement dans `./reasoner-legacy/reasoner-legacy.js` (un seul fichier généré par
// esbuild). Son état est un gros objet modifié en place : au lieu de le copier, on le RECONSTRUIT pour chaque
// ligne en rejouant les verdicts gardés (création, puis un enregistrement par ligne gardée). La feuille du moteur
// v2 reste immuable : chaque ligne gardée porte la liste des verdicts jusqu'à elle (`.legacy`).
// ============================================================================

import {
  commit,
  createState,
  goalReached,
  judge,
  kernelLineAtoms,
  krecLineAtoms,
  lex,
  normalize,
} from './reasoner-legacy/reasoner-legacy.js';

/** La sorte d'une feuille jouée par l'ancien moteur complet, ou null. */
export function legacyReplaySort(model) {
  const K = model.kernel;
  if (!K) return null;
  if (K.system) return 'system';
  if (K.induction) return 'induction';
  if (K.plugin === 'bounds') return 'bounds';
  return null;
}

const atomsOf = (model, text) =>
  model.bounds
    ? normalize(lex(text))
    : model.kernel?.induction
      ? krecLineAtoms(text)
      : kernelLineAtoms(text);

function rebuild(model, verdicts) {
  const st = createState(model);
  for (const v of verdicts) commit(st, v);
  return st;
}

/** Le texte de départ affiché : la 1re équation, la définition de la suite ou l'écriture à encadrer. */
export function legacyReplayStartText(model) {
  return String(model.kernel.start ?? '');
}

/** Juge une ligne ; `verdicts` = les verdicts des lignes gardées avant elle. */
export function judgeLegacyReplayLine(model, verdicts, text) {
  let v;
  let st;
  try {
    st = rebuild(model, verdicts);
    const chips = atomsOf(model, String(text)).map((a) => ({ atoms: [a], text: a }));
    v = judge(st, chips, { verify: true });
  } catch (e) {
    return {
      title: null,
      status: 'parse_error',
      kind: null,
      message: e instanceof Error ? e.message : String(e),
    };
  }
  const base = { title: v.title ?? null, message: v.message ?? null };
  const keep = () => {
    commit(st, v);
    return [...verdicts, v];
  };
  if (v.kind === 'step' || v.kind === 'ready') {
    const legacy = keep();
    const done = goalReached(st);
    const rule = v.rule;
    const gestures = rule
      ? [{ ruleId: rule.id, label: rule.label, why: rule.why, hora: rule.hora ?? 0 }]
      : [];
    return {
      ...base,
      status: done ? 'goal' : 'step',
      kind: 'step',
      gestures,
      hora: v.hora ?? rule?.hora ?? 0,
      message:
        v.message ?? (done ? 'Tu tiens la réponse !' : rule ? `Juste : ${rule.label}.` : 'Juste.'),
      legacy,
    };
  }
  if (v.kind === 'useless')
    return { ...base, status: 'useless', kind: 'kept', gestures: [], hora: 0, legacy: keep() };
  if (v.kind === 'unjustified')
    return { ...base, status: 'unjustified', kind: 'kept', gestures: [], hora: 0 };
  if (v.kind === 'false' || v.kind === 'trap')
    return { ...base, status: v.kind === 'trap' ? 'trap' : 'wrong', kind: null };
  return {
    ...base,
    status: 'parse_error',
    kind: null,
    message: v.message ?? 'Termine ta ligne.',
  };
}
